/**
 * Minimal HTML sanitizer for rich-text widgets. Runs at render time,
 * including inside Cloudflare Workers where DOMParser isn't available,
 * so it's regex-based rather than DOM-based.
 *
 * Allowlist: p, br, strong, b, em, i, u, s, a, ul, ol, li, span.
 * All `on*` event handler attributes are stripped. `javascript:`
 * URLs in href are neutralised. Anchor links gain a safe rel value.
 *
 * This is defense-in-depth: the TipTap editor already emits only
 * schema-allowed tags, but pages can be imported from external JSON
 * and must not be able to inject scripts.
 */
const ALLOWED_TAGS = new Set([
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "a",
    "ul",
    "ol",
    "li",
    "span",
]);
const ALLOWED_ATTRS_BY_TAG = {
    a: new Set(["href", "target", "rel", "title"]),
    span: new Set(["class"]),
};
function sanitizeAttributes(tag, rawAttrs) {
    const allowed = ALLOWED_ATTRS_BY_TAG[tag];
    if (!allowed)
        return "";
    const attrs = [];
    const re = /([a-zA-Z-]+)\s*=\s*"([^"]*)"|([a-zA-Z-]+)\s*=\s*'([^']*)'/g;
    let match = re.exec(rawAttrs);
    while (match !== null) {
        const name = (match[1] ?? match[3] ?? "").toLowerCase();
        const value = match[2] ?? match[4] ?? "";
        if (allowed.has(name)) {
            if (name === "href") {
                // Strip any URL scheme that could execute code.
                const trimmed = value.trim().toLowerCase();
                if (trimmed.startsWith("javascript:") ||
                    trimmed.startsWith("data:") ||
                    trimmed.startsWith("vbscript:")) {
                    match = re.exec(rawAttrs);
                    continue;
                }
            }
            // Escape double quotes in value so the serialized attr is safe.
            const safe = value.replace(/"/g, "&quot;");
            attrs.push(`${name}="${safe}"`);
        }
        match = re.exec(rawAttrs);
    }
    // Force safe rel on external-looking anchors.
    if (tag === "a") {
        const hasTargetBlank = /target\s*=\s*"_blank"/i.test(rawAttrs);
        if (hasTargetBlank) {
            const hasRel = attrs.some((a) => a.startsWith("rel="));
            if (!hasRel)
                attrs.push(`rel="noopener noreferrer"`);
        }
    }
    return attrs.length ? ` ${attrs.join(" ")}` : "";
}
export function sanitizeRichHtml(html) {
    if (!html)
        return "";
    // Drop any <script>, <style>, comments wholesale.
    let out = html
        .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "")
        .replace(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi, "")
        .replace(/<!--[\s\S]*?-->/g, "");
    // Replace every tag with its sanitized form. Unknown tags are dropped
    // but their children are preserved (so removing a stray <div> doesn't
    // nuke the text inside it).
    out = out.replace(/<(\/)?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (_full, close, name, attrs) => {
        const tag = name.toLowerCase();
        if (!ALLOWED_TAGS.has(tag))
            return "";
        if (close)
            return `</${tag}>`;
        return `<${tag}${sanitizeAttributes(tag, attrs)}>`;
    });
    return out;
}
/** True if the string appears to contain HTML markup (cheap heuristic). */
export function looksLikeHtml(s) {
    return /<[a-zA-Z][^>]*>/.test(s);
}
//# sourceMappingURL=sanitize.js.map